import { AppTopbar } from '@/layout/components/app.topbar';
import { LayoutService } from '@/layout/service/layout.service';

import {
  Component,
  computed,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { MeProfile } from '../../core/services/profile/me-profile.model';
import { WhitelabelService } from '../../core/services/whitelabel/whitelabel.service';
import { HomeState } from '../../modules/home/core/state/home.state';
import { ContrastColorPipe } from '../../shared/pipes/contrast-color.pipe';
import { ContrastService } from '../../shared/services/contrast.service';
import { AppMenu } from './app.menu';

@Component({
  selector: '[app-sidebar]',
  standalone: true,
  imports: [AppMenu, RouterModule, AppTopbar],
  providers: [ContrastColorPipe],
  template: ` @if (!hideTopbar) {
    <div
      class="layout-sidebar"
      (mouseenter)="onMouseEnter()"
      (mouseleave)="onMouseLeave()"
    >
      <div class="sidebar-header">
        <a class="logo" [routerLink]="['/']">
          @if (logoUrl()) {
            <img class="logo-image" [src]="logoUrl()" alt="Logo" />
          }
          <!-- <span
        class="app-name text-4xl font-medium leading-normal"
        [style.color]="contrastService.useWhiteText() ? '#ffffff' : '#000000'"
        >{{ me()?.whitelabel?.title }}</span
        > -->
        </a>
        <button
          class="layout-sidebar-anchor z-2"
          type="button"
          (click)="anchor()"
        ></button>
      </div>
      <div #menuContainer class="layout-menu-container">
        <div app-menu></div>
      </div>
      @if (isHorizontal() && !layoutService.isMobile()) {
        <div app-topbar></div>
      }
    </div>
  }`,
})
export class AppSidebar {
  private readonly whitelabelService = inject(WhitelabelService);
  private readonly store = inject(Store);
  protected readonly contrastService = inject(ContrastService);

  timeout: any = null;

  isHorizontal = computed(() => this.layoutService.isHorizontal());
  isDarkTheme = computed(() => this.layoutService.isDarkTheme());
  logoUrl = computed(
    () => this.whitelabelService.whitelabelConfig()?.files?.['header_logo.png'],
  );
  me = this.store.selectSignal<MeProfile>(HomeState.getMe);

  @ViewChild('menuContainer') menuContainer!: ElementRef;

  constructor(
    public layoutService: LayoutService,
    public el: ElementRef,
    private router: Router,
  ) {}

  get hideTopbar(): boolean {
    return this.router.url.includes('mosaic-view');
  }

  getLuminance(color: string): number {
    let rgb: number[];

    if (color.startsWith('#')) {
      rgb = color
        .replace('#', '')
        .match(/.{2}/g)!
        .map((v) => parseInt(v, 16) / 255);
    } else {
      rgb = color.match(/\d+/g)!.map((v) => parseInt(v) / 255);
    }

    const toLinear = (c: number) =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

    return (
      0.2126 * toLinear(rgb[0]) +
      0.7152 * toLinear(rgb[1]) +
      0.0722 * toLinear(rgb[2])
    );
  }

  getContrastRatio(luminance1: number, luminance2: number): number {
    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);
    return (lighter + 0.05) / (darker + 0.05);
  }

  onMouseEnter() {
    if (!this.layoutService.layoutState().anchored) {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }

      this.layoutService.layoutState.update((state) => {
        if (!state.sidebarActive) {
          return { ...state, sidebarActive: true };
        }
        return state;
      });
    }
  }

  onMouseLeave() {
    if (!this.layoutService.layoutState().anchored) {
      if (!this.timeout) {
        this.timeout = setTimeout(() => {
          this.layoutService.layoutState.update((state) => {
            if (state.sidebarActive) {
              return { ...state, sidebarActive: false };
            }
            return state;
          });
        }, 300);
      }
    }
  }

  anchor() {
    this.layoutService.layoutState.update((state) => ({
      ...state,
      anchored: !state.anchored,
    }));
  }
}
